import { useCallback, useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ClipboardList, Search, FileDown, ArrowUpDown, ListChecks, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { axiosInstance } from "@/App";
import { CooperativesBulkUpdate } from "@/components/CooperativesBulkUpdate";

const ALL = "__all__";
const PAGE_SIZES = [10, 20, 100];

const STATUS = {
  active: { label: "Ativo", dot: "bg-green-500", chip: "bg-green-50 text-green-800 border-green-200" },
  pending: { label: "Pendente", dot: "bg-yellow-500", chip: "bg-yellow-50 text-yellow-800 border-yellow-200" },
  sent_for_signature: { label: "Enviado p/ Assinatura", dot: "bg-blue-500", chip: "bg-blue-50 text-blue-800 border-blue-200" },
  suspended: { label: "Suspenso", dot: "bg-orange-500", chip: "bg-orange-50 text-orange-800 border-orange-200" },
  missing_login: { label: "Falta de Login", dot: "bg-purple-500", chip: "bg-purple-50 text-purple-800 border-purple-200" },
  modality_change: { label: "Troca de Modalidade", dot: "bg-cyan-500", chip: "bg-cyan-50 text-cyan-800 border-cyan-200" },
  awaiting_ownership_transfer: { label: "Aguard. Troca Tit.", dot: "bg-indigo-500", chip: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  ownership_transfer_requested: { label: "Troca Solicitada", dot: "bg-fuchsia-500", chip: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200" },
  cancelled: { label: "Cancelado", dot: "bg-red-500", chip: "bg-red-50 text-red-800 border-red-200" },
  excluded: { label: "Excluído", dot: "bg-gray-500", chip: "bg-gray-100 text-gray-700 border-gray-200" },
};
const statusInfo = (s) => STATUS[s] || { label: s || "—", dot: "bg-gray-400", chip: "bg-gray-50 text-gray-700 border-gray-200" };

const modalityShort = (m) => {
  if (!m) return "Compartilhada";
  if (m.startsWith("Autoconsumo Remoto - Usina")) return "Autoconsumo (Usina)";
  if (m.startsWith("Autoconsumo C")) return "Autoconsumo C";
  if (m.startsWith("Autoconsumo")) return "Autoconsumo Remoto";
  return "Geração Compartilhada";
};

const kwh = (n) => Number(n || 0).toLocaleString("pt-BR");

const APPORTIONMENT = ["Aguardando", "Pedido de Troca", "Incluido", "Excluido", "Pausado"];
const apportionmentClass = (s) => ({
  Aguardando: "bg-yellow-50 border-yellow-300 text-yellow-800",
  "Pedido de Troca": "bg-blue-50 border-blue-300 text-blue-800",
  Incluido: "bg-green-50 border-green-300 text-green-800",
  Excluido: "bg-red-50 border-red-300 text-red-800",
  Pausado: "bg-gray-100 border-gray-300 text-gray-700",
}[s] || "");

export const StatusBadge = ({ situation }) => {
  const s = statusInfo(situation);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium border rounded-full px-2 py-0.5 ${s.chip}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

export default function CooperativesManagement({ user, onLogout }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [fDist, setFDist] = useState(ALL);
  const [fStatus, setFStatus] = useState(ALL);
  const [fMod, setFMod] = useState(ALL);
  const [fApp, setFApp] = useState(ALL);
  const [fGroup, setFGroup] = useState(ALL);
  const [sort, setSort] = useState({ key: "name", dir: 1 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [meta, setMeta] = useState({ total: 0, pages: 1, totals: {}, filters: { distributors: [], situations: [], modalities: [] } });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [ucDraft, setUcDraft] = useState({});
  const reqId = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const params = {
        page, page_size: pageSize, sort: sort.key, dir: sort.dir,
        ...(debounced ? { search: debounced } : {}),
        ...(fDist !== ALL ? { distributor: fDist } : {}),
        ...(fStatus !== ALL ? { situation: fStatus } : {}),
        ...(fMod !== ALL ? { modality: fMod } : {}),
        ...(fApp !== ALL ? { apportionment: fApp } : {}),
        ...(fGroup !== ALL ? { consumer_group: fGroup } : {}),
      };
      const r = await axiosInstance.get("/cooperatives/management", { params });
      if (id !== reqId.current) return;
      setRows(r.data.items || []);
      setMeta((m) => ({ total: r.data.total, pages: r.data.pages, totals: r.data.totals || {}, filters: r.data.filters || m.filters }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar cooperados");
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [page, pageSize, sort, debounced, fDist, fStatus, fMod, fApp, fGroup]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [debounced, fDist, fStatus, fMod, fApp, pageSize]);

  const patchRow = async (id, payload, okMsg) => {
    try {
      const r = await axiosInstance.patch(`/cooperatives/${id}/management`, payload);
      const { ok, ...changes } = r.data || {};
      setRows((prev) => prev.map((x) => (x.id === id ? { ...x, ...changes } : x)));
      toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar cooperado");
    }
  };

  const toggleSort = (key) => setSort((p) => ({ key, dir: p.key === key ? -p.dir : 1 }));

  const exportCsv = () => {
    const head = ["Nome do Cooperado", "UC", "Distribuidora", "Grupo", "Consumo (kWh)", "Status", "Modalidade", "Status Rateio", "UC Geradora", "Usina"];
    const lines = rows.map((r) => [
      r.name, r.installation_number, r.distributor, r.consumer_group || "Grupo B", r.contracted_kwh,
      statusInfo(r.situation).label, r.compensation_modality,
      r.apportionment_status || "Aguardando", r.generator_installation_number || "", r.powerplant_name || "",
    ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";"));
    const blob = new Blob(["\uFEFF" + [head.join(";"), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Gestao_Cooperados_pag${page}_${new Date().toLocaleDateString("pt-BR").replace(/\//g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const SortHead = ({ k, children, className = "" }) => (
    <TableHead className={`cursor-pointer select-none whitespace-nowrap ${className}`} onClick={() => toggleSort(k)} data-testid={`mgmt-sort-${k}`}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown className={`w-3 h-3 ${sort.key === k ? "text-blue-700" : "opacity-40"}`} /></span>
    </TableHead>
  );

  const t = meta.totals || {};
  const from = meta.total ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(page * pageSize, meta.total);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-5" data-testid="cooperatives-management-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-blue-700" /> Gestão de Cooperados
          </h1>
          <p className="text-sm text-gray-500">Visão consolidada de todas as unidades consumidoras cadastradas.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Cooperados", t.count ?? 0, "mgmt-kpi-count"],
            ["Consumo Total", `${kwh(t.kwh)} kWh`, "mgmt-kpi-kwh"],
            ["Ativos", t.active ?? 0, "mgmt-kpi-active"],
            ["Autoconsumo", t.auto ?? 0, "mgmt-kpi-auto"],
          ].map(([label, value, tid]) => (
            <Card key={tid} className="border-l-4 border-l-blue-600">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
                <p className="text-xl font-bold text-gray-900" data-testid={tid}>{value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome, UC, CPF/CNPJ ou parceiro…"
                  className="pl-8 h-9" data-testid="mgmt-search-input" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Select value={fDist} onValueChange={setFDist}>
                  <SelectTrigger className="h-9 w-44 text-xs" data-testid="mgmt-filter-distributor"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Todas as distribuidoras</SelectItem>
                    {meta.filters.distributors.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={fStatus} onValueChange={setFStatus}>
                  <SelectTrigger className="h-9 w-44 text-xs" data-testid="mgmt-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Todos os status</SelectItem>
                    {meta.filters.situations.map((s) => <SelectItem key={s} value={s}>{statusInfo(s).label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={fMod} onValueChange={setFMod}>
                  <SelectTrigger className="h-9 w-48 text-xs" data-testid="mgmt-filter-modality"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Todas as modalidades</SelectItem>
                    {meta.filters.modalities.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={fGroup} onValueChange={setFGroup}>
                  <SelectTrigger className="h-9 w-36 text-xs" data-testid="mgmt-filter-group"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Todos os grupos</SelectItem>
                    <SelectItem value="Grupo A">Grupo A</SelectItem>
                    <SelectItem value="Grupo B">Grupo B</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={fApp} onValueChange={setFApp}>
                  <SelectTrigger className="h-9 w-44 text-xs" data-testid="mgmt-filter-apportionment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Todos os rateios</SelectItem>
                    {APPORTIONMENT.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-9" onClick={exportCsv} data-testid="mgmt-export-csv-btn">
                  <FileDown className="w-4 h-4 mr-1" /> CSV
                </Button>
                <Button size="sm" className="h-9 bg-blue-700 hover:bg-blue-800" onClick={() => setBulkOpen(true)} data-testid="mgmt-bulk-update-btn">
                  <ListChecks className="w-4 h-4 mr-1" /> Atualização em Massa
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table data-testid="mgmt-table">
                <TableHeader>
                  <TableRow>
                    <SortHead k="name">Nome do Cooperado</SortHead>
                    <SortHead k="installation_number">UC</SortHead>
                    <SortHead k="distributor">Distribuidora</SortHead>
                    <SortHead k="consumer_group">Grupo</SortHead>
                    <SortHead k="contracted_kwh" className="text-right">Consumo (kWh)</SortHead>
                    <SortHead k="situation">Status</SortHead>
                    <SortHead k="compensation_modality">Modalidade</SortHead>
                    <SortHead k="apportionment_status">Status Rateio</SortHead>
                    <SortHead k="generator_installation_number">UC da Usina</SortHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && rows.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center text-sm text-gray-500 py-8">Carregando…</TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center text-sm text-gray-500 py-8" data-testid="mgmt-empty">Nenhum cooperado encontrado.</TableCell></TableRow>
                  ) : rows.map((r) => (
                    <TableRow key={r.id} data-testid={`mgmt-row-${r.id}`} className={loading ? "opacity-60" : ""}>
                      <TableCell className="font-medium text-sm">
                        {r.name}
                        {r.partner_name && <div className="text-xs text-gray-400">Parceiro: {r.partner_name}</div>}
                      </TableCell>
                      <TableCell className="text-sm font-mono">{r.installation_number || "—"}</TableCell>
                      <TableCell className="text-sm">{r.distributor || "—"}</TableCell>
                      <TableCell>
                        <Select value={r.consumer_group === "Grupo A" ? "Grupo A" : "Grupo B"}
                          onValueChange={(v) => patchRow(r.id, { consumer_group: v }, `Grupo: ${v}`)}>
                          <SelectTrigger className={`h-8 w-28 text-xs font-semibold ${r.consumer_group === "Grupo A" ? "bg-indigo-50 text-indigo-800 border-indigo-200" : "bg-slate-50 text-slate-700 border-slate-200"}`}
                            data-testid={`mgmt-group-${r.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Grupo A">Grupo A</SelectItem>
                            <SelectItem value="Grupo B">Grupo B</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-right">{kwh(r.contracted_kwh)}</TableCell>
                      <TableCell><StatusBadge situation={r.situation} /></TableCell>
                      <TableCell className="text-sm">{modalityShort(r.compensation_modality)}</TableCell>
                      <TableCell>
                        <Select value={r.apportionment_status || "Aguardando"}
                          onValueChange={(v) => patchRow(r.id, { apportionment_status: v }, `Status Rateio: ${v}`)}>
                          <SelectTrigger className={`h-8 w-40 text-xs font-medium ${apportionmentClass(r.apportionment_status || "Aguardando")}`}
                            data-testid={`mgmt-apportionment-${r.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {APPORTIONMENT.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={ucDraft[r.id] ?? (r.generator_installation_number || "")}
                          onChange={(e) => setUcDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                          onBlur={() => {
                            const v = (ucDraft[r.id] ?? "").trim();
                            if (ucDraft[r.id] === undefined || v === (r.generator_installation_number || "")) return;
                            patchRow(r.id, { generator_installation_number: v }, v ? "UC da usina atualizada" : "UC da usina removida");
                            setUcDraft((d) => { const n = { ...d }; delete n[r.id]; return n; });
                          }}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          placeholder="UC da geradora"
                          className="h-8 w-40 text-xs font-mono"
                          data-testid={`mgmt-generator-uc-input-${r.id}`}
                        />
                        {r.powerplant_name && (
                          <div className="text-[11px] text-gray-500 mt-0.5" data-testid={`mgmt-plant-name-${r.id}`}>{r.powerplant_name}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span>Por página:</span>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className="h-8 w-20 text-xs" data-testid="mgmt-page-size"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span data-testid="mgmt-footer-count">Exibindo {from}–{to} de {meta.total} cooperado(s)</span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-8" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} data-testid="mgmt-prev-page">
                  <ChevronLeft className="w-4 h-4" /> Anterior
                </Button>
                <span className="text-xs text-gray-600 px-2" data-testid="mgmt-page-indicator">Página {page} de {meta.pages}</span>
                <Button variant="outline" size="sm" className="h-8" disabled={page >= meta.pages || loading} onClick={() => setPage((p) => p + 1)} data-testid="mgmt-next-page">
                  Próxima <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        <CooperativesBulkUpdate open={bulkOpen} onOpenChange={setBulkOpen} onApplied={load} />
      </div>
    </Layout>
  );
}
